import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import type {
  NormalizedCart,
  Generation,
  ProductType,
  Style,
} from "../types";
import {
  addToCart,
  applyCartDiscount,
  assetUrl,
  fetchLivePrices,
  getCart,
  removeCartLine,
  updateCartLine,
} from "../api";
import { PRODUCTS, getProduct } from "../data/products";
import { FlatArtPreview } from "./FlatArtPreview";
import { t } from "../i18n";
import type { STRINGS } from "../i18n/strings";

type I18nKey = keyof typeof STRINGS;

// Widget mode (mounted inside a Shopify PDP) — read product context from the
// global the PDP Liquid injects (window.InujoyProduct). Model B: the PDP
// product is a STYLE (design), not a format. Format / size / frame are picked
// inside the widget. So PdpProductInfo carries only the style binding +
// shop metadata; format details come from the in-widget picker.
interface PdpProductInfo {
  productId?: string;
  productHandle?: string;
  title?: string;
  currencyCode?: string;
}
function getPdpProduct(): PdpProductInfo | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { InujoyProduct?: PdpProductInfo }).InujoyProduct;
  return g && g.productHandle ? g : null;
}

// ── Signature font catalog ─────────────────────────────────────
// One font per visual register so the picker stays simple. font_id is the
// stable key sent to the backend; fontFamily is the CSS family. These MUST
// stay in sync with the Google Fonts <link> in web/index.html AND in
// inujoy-theme/sections/inujoy-dog-customizer.liquid (the storefront loads
// the fonts from there, not index.html).
// Curated to 3 authentic signature fonts. Removed Banner (block-caps, not a
// signature) + Service (typewriter, not a signature). Honor is the default
// because Great Vibes is the most "chữ ký" — elegant flowing script.
export const SIGNATURE_FONTS = [
  { id: "great_vibes",      labelKey: "preview.sig.font.honor"       as I18nKey, fontFamily: "Great Vibes",      group: "Formal" },
  { id: "caveat",           labelKey: "preview.sig.font.handwritten" as I18nKey, fontFamily: "Caveat",           group: "Hand" },
  { id: "permanent_marker", labelKey: "preview.sig.font.marker"      as I18nKey, fontFamily: "Permanent Marker", group: "Bold" },
] as const;
export type SignatureFontId = (typeof SIGNATURE_FONTS)[number]["id"];

function fontFamilyFor(id: SignatureFontId): string {
  return SIGNATURE_FONTS.find((f) => f.id === id)?.fontFamily ?? "Great Vibes";
}

// User-tunable size multiplier on the auto-sized base. 1.0 = auto, range 0.5–2.5.
const SIG_SCALE_MIN = 0.5;
const SIG_SCALE_MAX = 2.5;
const SIG_SCALE_STEP = 0.05;

// ── Cart persistence ───────────────────────────────────────────
// Storefront-API carts live server-side at Shopify; we only need to remember
// which cart this browser owns so adds accumulate instead of creating a new
// cart each time. localStorage survives "Start over" and page reloads.
const CART_ID_KEY = "inujoy.cart_id";
function loadCartId(): string | null {
  try { return localStorage.getItem(CART_ID_KEY); } catch { return null; }
}
function saveCartId(id: string): void {
  try { localStorage.setItem(CART_ID_KEY, id); } catch { /* private mode — ignore */ }
}
function clearCartId(): void {
  try { localStorage.removeItem(CART_ID_KEY); } catch { /* ignore */ }
}

function fmtPrice(amount: string): string {
  const n = Number(amount);
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

interface Props {
  generation: Generation;
  style: Style;
  onRestart: () => void;
  /** Widget mode (PDP) hides the in-card "Add to Cart" — the PDP's own
   *  Add to Cart button drives via the InujoyWidget API instead. */
  hideAddToCartButton?: boolean;
}

export interface PreviewStepHandle {
  /** Trigger the same add-to-cart flow the internal button uses. Returns
   *  the resulting checkoutUrl if successful, undefined otherwise. */
  addToCart: () => Promise<string | undefined>;
}

export const PreviewStep = forwardRef<PreviewStepHandle, Props>(function PreviewStep(
  { generation, style, onRestart, hideAddToCartButton },
  ref,
) {
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  // Persistent Shopify cart — accumulates across adds. Restored from
  // localStorage on mount so adding from a second dad keeps the same cart.
  const [cart, setCart] = useState<NormalizedCart | null>(null);
  // Order-confirm overlay visibility. Shopify's hosted checkout can't render
  // the AI mockup, so we show the cart here before handing off to checkout.
  const [cartOpen, setCartOpen] = useState(false);
  // Set to true right after a successful "Add to Cart" so the overlay shows
  // a MockupCarousel of the just-added item (with signature + swipe). Goes
  // false on "Keep customizing" so reopening the cart shows the line list only.
  const [showJustAddedHero, setShowJustAddedHero] = useState(false);
  // Which line is expanded for "view back" — shows a larger preview of that
  // line's composited mockup (signature already baked into thumbnailUrl).
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  // True while a qty/remove mutation is in flight (disables the steppers).
  const [cartBusy, setCartBusy] = useState(false);

  // Discount code state
  const [discountInput, setDiscountInput] = useState("");
  const [discountBusy, setDiscountBusy] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Widget-mode detection — when mounted inside a PDP, the product type +
  // size + frame come from Shopify (variant). We hide the in-widget pickers
  // and use the PDP's product type as the default for thumbnail compositing.
  const pdpProduct = getPdpProduct();
  const widgetMode = pdpProduct !== null;

  // Product selection state. Model B: customer always picks the format here
  // (style PDP doesn't bind a format). Default to photo_panel — the main
  // entry product and cheapest format.
  const [activeType, setActiveType] = useState<ProductType>("photo_panel");
  const [activeSizeIdx, setActiveSizeIdx] = useState(0);
  const [activeFrameColor, setActiveFrameColor] = useState<string>("Black");

  // Signature state — opt-in. Default OFF; customer expands to add.
  const [signatureOn, setSignatureOn] = useState(false);
  const [signatureText, setSignatureText] = useState(generation.dad_name);
  // Default to Honor (Great Vibes) — most signature-like of the 3 fonts.
  const [signatureFontId, setSignatureFontId] = useState<SignatureFontId>("great_vibes");
  const [signatureScale, setSignatureScale] = useState(1);
  const activeSignature = signatureOn && signatureText.trim()
    ? { text: signatureText.trim(), fontFamily: fontFamilyFor(signatureFontId), scale: signatureScale }
    : null;

  const product = getProduct(activeType);
  const activeSize = product.sizes[activeSizeIdx] ?? product.sizes[0];
  // Only render a picker when there's an actual choice. With one visible
  // product / one size it's not a choice — just clutter (the default is used).
  const visibleProducts = PRODUCTS.filter((p) => !p.hidden);

  // Live Shopify prices (source of truth) — override the bundled retailPrice
  // so the picker never drifts from what Shopify charges. Falls back to the
  // bundled price per (type, size index) when no live price is available.
  const [livePrices, setLivePrices] = useState<Record<string, (number | null)[]>>({});
  useEffect(() => {
    fetchLivePrices().then(setLivePrices).catch(() => { /* keep fallbacks */ });
  }, []);
  const priceOf = (type: string, sizeIdx: number, fallback: number): number =>
    livePrices[type]?.[sizeIdx] ?? fallback;

  // Reset size index when switching products (different size lists)
  useEffect(() => {
    setActiveSizeIdx(0);
    if (product.frameColors && product.frameColors.length > 0) {
      setActiveFrameColor(product.frameColors[0] ?? "Black");
    }
  }, [activeType]);

  // Restore the persisted cart on mount so a second dad's "Add to Cart"
  // accumulates into the same cart. A 404 (expired) drops the stale id.
  useEffect(() => {
    const id = loadCartId();
    if (!id) return;
    let cancelled = false;
    getCart(id)
      .then((c) => {
        if (cancelled) return;
        if (c && c.totalQuantity > 0) setCart(c);
        else clearCartId();
      })
      .catch(() => { /* network hiccup — keep the id, retry on next add */ });
    return () => { cancelled = true; };
  }, []);

  /** Returns the checkout URL on success, undefined on failure. Used both
   *  by the in-card "Add to Cart" button and (via useImperativeHandle) by
   *  the PDP's external Add to Cart button. */
  async function handleAddToCart(): Promise<string | undefined> {
    setCartError(null);
    setCartLoading(true);
    try {
      const existingId = loadCartId();
      // Model B: backend resolves the format-product variant from
      // (product_type, size_index, frame_color) via Storefront API lookup.
      // We intentionally DO NOT pass window.InujoyProduct.variantId —
      // that variant belongs to the STYLE product the customer is browsing,
      // not the format (Photo Panel / Gallery Canvas / ...) they picked
      // here. Letting backend resolve keeps cart line and order data
      // consistent with the format the customer actually chose.
      const res = await addToCart({
        generation_id: generation.id,
        product_type: activeType,
        size_index: activeSizeIdx,
        ...(product.frameColors ? { frame_color: activeFrameColor } : {}),
        ...(existingId ? { cart_id: existingId } : {}),
        ...(activeSignature
          ? {
              signature: {
                text: activeSignature.text,
                font_id: signatureFontId,
                scale: signatureScale,
              },
            }
          : {}),
      });
      saveCartId(res.cart.cartId);
      setCart(res.cart);
      // Just-added: show the MockupCarousel hero (with signature + swipe).
      setShowJustAddedHero(true);
      setCartOpen(true);
      setCartLoading(false);

      // Mirror to Shopify's theme cart so the header cart icon + /cart page
      // stay in sync. Best-effort: a network failure here doesn't roll back
      // the Storefront cart (the customer can still checkout via the PDP
      // "GO TO CHECKOUT →" button).
      //
      // Model B: use the FORMAT variant the backend resolved (matches the
      // customer's actual choice). Falling back to window.InujoyProduct
      // .variantIdNumeric would mirror the style PDP's placeholder variant
      // — different from what's in the Storefront cart, breaking the
      // header icon + /cart page sync.
      const mirrorVariantNumeric = res.resolvedVariantIdNumeric ?? null;
      if (mirrorVariantNumeric) {
        // Build properties on frontend so this works even if the api hasn't
        // been redeployed yet with the lineProperties response field. The
        // backend version (res.lineProperties) is preferred when available
        // (it includes the composited _thumbnail_url) — overrides our base.
        const baseProps: Record<string, string> = {
          "Dad Portrait": `${generation.dad_name} the ${style.label}`,
          _ai_image_url: generation.preview_url ?? "",
          _generation_id: generation.id,
          _dad_name: generation.dad_name,
          _style_id: generation.style_id,
        };
        if (activeSignature) {
          baseProps._signature_text = activeSignature.text;
          baseProps._signature_font = signatureFontId;
          baseProps._signature_scale = String(signatureScale);
        }
        const properties = { ...baseProps, ...(res.lineProperties ?? {}) };
        console.log("[theme-cart] mirroring to /cart/add.js", {
          id: Number(mirrorVariantNumeric),
          properties,
        });
        void fetch("/cart/add.js", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            id: Number(mirrorVariantNumeric),
            quantity: 1,
            properties,
          }),
        })
          .then(async (r) => {
            if (!r.ok) {
              const text = await r.text().catch(() => "");
              console.warn(
                `[theme-cart] /cart/add.js ${r.status} — ${text.slice(0, 200)}`,
              );
              return;
            }
            const item = await r.json().catch(() => null);
            console.log("[theme-cart] mirror OK, item:", item);
            // Nudge any theme cart-counter widgets to re-fetch. Horizon and
            // Dawn-based themes listen for one of these conventions; firing
            // all three covers the common cases.
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            document.dispatchEvent(new CustomEvent("cart:updated"));
            document.dispatchEvent(new CustomEvent("cart:change", { detail: item }));
          })
          .catch((e) => {
            console.warn("[theme-cart] mirror to /cart/add.js failed:", e);
          });
      }

      return res.cart.checkoutUrl;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setCartError(message);
      setCartLoading(false);
      return undefined;
    }
  }

  useImperativeHandle(ref, () => ({ addToCart: handleAddToCart }), [
    generation.id, activeType, activeSizeIdx, activeFrameColor,
    signatureFontId, signatureScale, activeSignature,
  ]);

  // Qty stepper / remove inside the overlay. quantity < 1 removes the line.
  async function handleLineQty(lineId: string, quantity: number) {
    if (!cart || cartBusy) return;
    setCartBusy(true);
    setCartError(null);
    try {
      const res =
        quantity < 1
          ? await removeCartLine({ cart_id: cart.cartId, line_id: lineId })
          : await updateCartLine({ cart_id: cart.cartId, line_id: lineId, quantity });
      setCart(res.cart);
      if (res.cart.totalQuantity === 0) clearCartId();
      // If the expanded line was removed, collapse.
      if (expandedLineId === lineId && quantity < 1) setExpandedLineId(null);
    } catch (e) {
      setCartError(e instanceof Error ? e.message : String(e));
    } finally {
      setCartBusy(false);
    }
  }

  async function handleApplyDiscount(e: React.FormEvent) {
    e.preventDefault();
    if (!cart || discountBusy) return;
    const code = discountInput.trim().toUpperCase();
    if (!code) return;
    setDiscountBusy(true);
    setDiscountError(null);
    try {
      // Shopify replaces the entire codes list — preserve the already-applied
      // codes (the ones it accepted last time) so the customer can stack.
      const existing = cart.discountCodes
        .filter((d) => d.applicable)
        .map((d) => d.code);
      const next = Array.from(new Set([...existing, code]));
      const res = await applyCartDiscount({ cart_id: cart.cartId, codes: next });
      setCart(res.cart);
      const rejected = res.cart.discountCodes.find(
        (d) => d.code === code && !d.applicable,
      );
      if (rejected) {
        setDiscountError(`Code "${code}" is not valid.`);
      } else {
        setDiscountInput("");
      }
    } catch (err) {
      setDiscountError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscountBusy(false);
    }
  }

  async function handleRemoveDiscount(code: string) {
    if (!cart || discountBusy) return;
    setDiscountBusy(true);
    setDiscountError(null);
    try {
      const next = cart.discountCodes
        .filter((d) => d.applicable && d.code !== code)
        .map((d) => d.code);
      const res = await applyCartDiscount({ cart_id: cart.cartId, codes: next });
      setCart(res.cart);
    } catch (err) {
      setDiscountError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscountBusy(false);
    }
  }

  const previewUrl = generation.preview_url;

  return (
    <div className="card reveal">
      {/* Flat reveal — the AI portrait as a single clean image. No mockup
          carousel / 3D-wrap compositing; plain <img>, renders on any origin. */}
      {previewUrl && (
        <div className="product-display">
          <FlatArtPreview src={previewUrl} signature={activeSignature} />
        </div>
      )}

      {/* ─── Optional signature personalization ─── */}
      <div className={"signature-block" + (signatureOn ? " is-open" : "")}>
        <button
          type="button"
          className="signature-toggle"
          onClick={() => setSignatureOn((v) => !v)}
          aria-expanded={signatureOn}
        >
          <span className="signature-toggle__icon">✍</span>
          <span className="signature-toggle__label">
            {signatureOn ? t("preview.sig.toggle_on") : t("preview.sig.toggle_off")}
          </span>
          <span className="signature-toggle__chev">{signatureOn ? "▴" : "▾"}</span>
        </button>
        {signatureOn && (
          <div className="signature-panel">
            <label className="signature-input-row">
              <span className="signature-input-row__label">{t("preview.sig.text_label")}</span>
              <input
                type="text"
                maxLength={30}
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                placeholder={t("preview.sig.placeholder")}
              />
            </label>

            <div className="signature-size-row">
              <div className="signature-size-row__label">
                <span>{t("preview.sig.size_label")}</span>
                <span className="signature-size-row__value">{Math.round(signatureScale * 100)}%</span>
              </div>
              <input
                type="range"
                min={SIG_SCALE_MIN}
                max={SIG_SCALE_MAX}
                step={SIG_SCALE_STEP}
                value={signatureScale}
                onChange={(e) => setSignatureScale(parseFloat(e.target.value))}
                className="signature-size-slider"
                aria-label={t("preview.sig.size_aria")}
              />
              <div className="signature-size-row__ticks">
                <span>A</span>
                <button
                  type="button"
                  className="signature-size-row__reset"
                  onClick={() => setSignatureScale(1)}
                  disabled={signatureScale === 1}
                  title={t("preview.sig.reset_title")}
                >↺</button>
                <span className="signature-size-row__big">A</span>
              </div>
            </div>

            <div className="signature-fonts">
              {SIGNATURE_FONTS.map((f) => {
                const label = t(f.labelKey);
                return (
                <button
                  type="button"
                  key={f.id}
                  className={"signature-font" + (signatureFontId === f.id ? " is-active" : "")}
                  onClick={() => setSignatureFontId(f.id)}
                  style={{ fontFamily: `"${f.fontFamily}", cursive` }}
                  title={`${label} · ${f.group}`}
                >
                  <span className="signature-font__preview">{signatureText || label}</span>
                  <span className="signature-font__name">{label}</span>
                </button>
                );
              })}
            </div>
            <p className="signature-hint">
              {t("preview.sig.hint")}
            </p>
          </div>
        )}
      </div>

      {/* Title / tagline / "Take X's legend home" header are redundant inside
          a Shopify PDP — the PDP already shows the product title, description,
          price. Hide them in widget mode to keep the embedded preview focused
          on the mockup + signature controls. */}
      {!widgetMode && (
        <>
          <h2>{generation.dad_name} the {style.label}</h2>
          <p className="sub">{style.tagline}</p>
        </>
      )}

      {/* ─── Product Selector ─── */}
      <div className="product-section">
        {!widgetMode && (
          <div className="product-section__header">
            Take {generation.dad_name}'s legend home
          </div>
        )}

        {/* Format picker — visible in BOTH standalone and widget (style PDP)
            modes. Model B: customer is on a style PDP (e.g. "InuJoy Hero")
            and picks the format here (Photo Panel). The chosen
            (type, size, frame_color) is sent to /cart,
            where the backend resolves the matching format product variant
            via Storefront API. */}
        <>
          {/* Product type tabs — only a real choice when 2+ formats are
              visible. With one format it's clutter, so hide it (activeType
              still defaults to the single product for add-to-cart). */}
          {visibleProducts.length > 1 && (
            <div className="product-tabs">
              {visibleProducts.map((p) => (
                <button
                  key={p.type}
                  className={"product-tab" + (activeType === p.type ? " active" : "")}
                  onClick={() => setActiveType(p.type)}
                >
                  <span className="product-tab__icon">
                    {p.type === "photo_panel" ? "🎁" : p.type === "gallery_canvas" ? "🖼️" : "🖼️"}
                  </span>
                  <span className="product-tab__label">{t(`product.${p.type}.label` as I18nKey)}</span>
                  <span className="product-tab__price">
                    {t("preview.format.from", { price: priceOf(p.type, 0, p.sizes[0]?.retailPrice ?? 0).toLocaleString() })}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Product info — redundant inside a Shopify PDP (which already shows
              the title, description and pdp-* feature sections). Standalone
              mode keeps it. */}
          {!widgetMode && (
            <div className="product-details">
              <div className="product-details__name">{t(`product.${product.type}.label` as I18nKey)}</div>
              <div className="product-details__subtitle">{t(`product.${product.type}.subtitle` as I18nKey)}</div>
              <div className="product-details__material">{t(`product.${product.type}.material` as I18nKey)}</div>
              <ul className="product-details__features">
                {product.features.map((_, i) => (
                  <li key={i}>✓ {t(`product.${product.type}.feature_${i + 1}` as I18nKey)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Frame color (if applicable) */}
          {product.frameColors && (
            <div className="option-group">
              <label className="option-label">{t("preview.format.frame_color")}</label>
              <div className="color-swatches">
                {product.frameColors.map((c) => {
                  const colorKey = ("product.color." + c.toLowerCase().replace(/\s+/g, "_")) as I18nKey;
                  const colorLabel = t(colorKey);
                  return (
                  <button
                    key={c}
                    className={"color-swatch" + (activeFrameColor === c ? " active" : "")}
                    onClick={() => setActiveFrameColor(c)}
                    title={colorLabel}
                    style={{
                      backgroundColor:
                        c === "Black" ? "#1a1a1a" :
                        c === "White" ? "#f5f5f0" :
                        c === "Walnut" ? "#5b3a1f" :
                        c === "Dark Brown" ? "#4a3728" :
                        c === "Natural" ? "#c8a882" : "#888",
                    }}
                  />
                  );
                })}
                <span className="color-name">
                  {t(("product.color." + activeFrameColor.toLowerCase().replace(/\s+/g, "_")) as I18nKey)}
                </span>
              </div>
            </div>
          )}

          {/* Size selector — only a real choice when there are 2+ sizes. With
              one size it's clutter, so hide it (activeSizeIdx defaults to 0). */}
          {product.sizes.length > 1 && (
            <div className="option-group">
              <label className="option-label">{t("preview.format.size")}</label>
              <div className="size-grid">
                {product.sizes.map((s, i) => (
                  <button
                    key={s.label}
                    className={"size-btn" + (activeSizeIdx === i ? " active" : "")}
                    onClick={() => setActiveSizeIdx(i)}
                  >
                    <span className="size-btn__label">{s.label}</span>
                    <span className="size-btn__price">¥{priceOf(activeType, i, s.retailPrice).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>


        {/* Price + CTA. In widget mode the displayed price comes from the
            PDP variant (Shopify is the source of truth); standalone reads it
            from the local products.ts catalog via activeSize.
            In widget mode, the entire price + cart bar is owned by the PDP
            (price label, ADD TO CART button) — hide ours to avoid duplication
            of the wonderme-style information shown above the widget. */}
        {!widgetMode && (
          <div className="checkout-bar">
            <div className="checkout-price">
              <span className="checkout-price__amount">
                ¥{priceOf(activeType, activeSizeIdx, activeSize?.retailPrice ?? 0).toLocaleString()}
              </span>
              <span className="checkout-price__shipping">
                {priceOf(activeType, activeSizeIdx, activeSize?.retailPrice ?? 0) >= 3980 ? t("preview.shipping.free") : t("preview.shipping.flat")}
              </span>
            </div>
            <button
              className={"btn btn-primary checkout-btn" + (cartLoading ? " loading" : "")}
              onClick={handleAddToCart}
              disabled={cartLoading}
            >
              {cartLoading ? t("preview.adding_to_cart") : t("preview.add_to_cart")}
            </button>
          </div>
        )}

        {/* Cart error + view-cart link still surface in widget mode so a
            failed add-to-cart (PDP button) isn't a silent dead-end. */}
        {cartError && !cartOpen && <div className="error">{cartError}</div>}

        {cart && cart.totalQuantity > 0 && !cartOpen && (
          <button
            type="button"
            className="view-cart-link"
            onClick={() => setCartOpen(true)}
          >
            {t("preview.view_cart", { n: cart.totalQuantity })}
          </button>
        )}
      </div>

      {!widgetMode && (
        <button className="btn btn-secondary" onClick={onRestart}>
          {t("preview.start_over")}
        </button>
      )}

      {/* Widget-mode (PDP): compact link to swap photo / retry without leaving
          the product page. Standalone mode already has the big "Start over"
          secondary button above. */}
      {widgetMode && (
        <button type="button" className="preview-change-photo" onClick={onRestart}>
          {t("preview.try_another")}
        </button>
      )}

      {/* ─── Order-confirm overlay — accumulating mini-cart ─── */}
      {cartOpen && cart && (() => {
        // Pre-discount subtotal — sum of lines' unitPrice × quantity. Shopify's
        // cart.subtotal is POST-discount, so we compute pre-discount locally
        // and derive the discount delta.
        const preDiscount = cart.lines.reduce(
          (sum, l) => sum + Number(l.unitPrice) * l.quantity,
          0,
        );
        const postDiscount = Number(cart.subtotal);
        const discountAmount = Math.max(0, preDiscount - postDiscount);
        const appliedCodes = cart.discountCodes.filter((d) => d.applicable);

        return (
        <div className="order-confirm" role="dialog" aria-modal="true">
          <div className="order-confirm__card">
            <h2 className="order-confirm__title">
              {cart.totalQuantity > 0 ? t("cart.added_title") : t("cart.empty_title")}
            </h2>

            {/* Just-added hero — full MockupCarousel of the current selection
                (signature baked in, swipe between scenes). Same component the
                product page uses, so the customer sees exactly what they'll
                receive. Hidden after "Keep customizing" / cart restore. */}
            {showJustAddedHero && previewUrl && cart.totalQuantity > 0 && (
              <div className="order-confirm__hero">
                <FlatArtPreview src={previewUrl} signature={activeSignature} />
              </div>
            )}

            {/* Line items — qty steppers + remove, click to expand thumbnail. */}
            {cart.lines.length > 0 && (
              <ul className="cart-lines">
                {cart.lines.map((line) => {
                  const isExpanded = expandedLineId === line.lineId;
                  const lineImg = line.thumbnailUrl || line.variantImage;
                  return (
                  <li
                    className={"cart-line" + (isExpanded ? " is-expanded" : "")}
                    key={line.lineId}
                  >
                    <div className="cart-line__main">
                      <button
                        type="button"
                        className="cart-line__thumb"
                        onClick={() =>
                          setExpandedLineId(isExpanded ? null : line.lineId)
                        }
                        title={isExpanded ? t("cart.line.collapse") : t("cart.line.expand")}
                        aria-expanded={isExpanded}
                      >
                        {lineImg && (
                          <img
                            src={assetUrl(lineImg)}
                            alt={line.dadPortrait || line.productTitle}
                          />
                        )}
                      </button>
                      <div className="cart-line__info">
                        <div className="cart-line__name">
                          {line.dadPortrait || line.productTitle}
                        </div>
                        <div className="cart-line__meta">
                          {line.productLabel || line.productTitle}
                        </div>
                        <div className="cart-line__qty">
                          <button
                            type="button"
                            className="qty-btn"
                            onClick={() => handleLineQty(line.lineId, line.quantity - 1)}
                            disabled={cartBusy}
                            aria-label={t("cart.line.qty_dec")}
                          >−</button>
                          <span className="qty-value">{line.quantity}</span>
                          <button
                            type="button"
                            className="qty-btn"
                            onClick={() => handleLineQty(line.lineId, line.quantity + 1)}
                            disabled={cartBusy}
                            aria-label={t("cart.line.qty_inc")}
                          >+</button>
                          <button
                            type="button"
                            className="cart-line__remove"
                            onClick={() => handleLineQty(line.lineId, 0)}
                            disabled={cartBusy}
                          >{t("cart.line.remove")}</button>
                        </div>
                      </div>
                      <div className="cart-line__price">{fmtPrice(line.totalPrice)}</div>
                    </div>

                    {isExpanded && lineImg && (
                      <div className="cart-line__expanded">
                        <img
                          src={assetUrl(lineImg)}
                          alt={line.dadPortrait || line.productTitle}
                        />
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            )}

            {/* Coupon / discount code */}
            {cart.totalQuantity > 0 && (
              <div className="cart-discount">
                {appliedCodes.length > 0 && (
                  <ul className="cart-discount__applied">
                    {appliedCodes.map((d) => (
                      <li key={d.code} className="cart-discount__chip">
                        <span>🏷 {d.code}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDiscount(d.code)}
                          disabled={discountBusy}
                          aria-label={t("cart.line.remove_aria", { code: d.code })}
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
                <form className="cart-discount__form" onSubmit={handleApplyDiscount}>
                  <input
                    type="text"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder={t("cart.discount.placeholder")}
                    disabled={discountBusy}
                    autoCapitalize="characters"
                  />
                  <button
                    type="submit"
                    className="btn-apply"
                    disabled={discountBusy || !discountInput.trim()}
                  >
                    {discountBusy ? "…" : t("cart.discount.apply")}
                  </button>
                </form>
                {discountError && (
                  <div className="cart-discount__error">{discountError}</div>
                )}
              </div>
            )}

            {/* Cost breakdown */}
            {cart.totalQuantity > 0 && (
              <div className="cart-totals">
                <div className="cart-totals__row">
                  <span>
                    {cart.totalQuantity === 1
                      ? t("cart.totals.subtotal.one")
                      : t("cart.totals.subtotal.many", { n: cart.totalQuantity })}
                  </span>
                  <span>{fmtPrice(String(preDiscount))}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="cart-totals__row cart-totals__row--discount">
                    <span>
                      {appliedCodes[0]
                        ? t("cart.totals.discount_with_code", { code: appliedCodes[0].code })
                        : t("cart.totals.discount")}
                    </span>
                    <span>−{fmtPrice(String(discountAmount))}</span>
                  </div>
                )}
                <div className="cart-totals__row cart-totals__row--total">
                  <span>{t("cart.totals.total")}</span>
                  <span className="cart-totals__amount">{fmtPrice(cart.subtotal)}</span>
                </div>
                <div className="cart-totals__shipping">{t("cart.totals.shipping_note")}</div>
              </div>
            )}

            {cartError && <div className="error">{cartError}</div>}

            {/* Sibling-discount nudge — empty string (the default) renders
                nothing; the owner activates it from the Theme Editor after
                creating the automatic discount. */}
            {cart.totalQuantity > 0 && t("cart.sibling_deal") !== "" && (
              <div className="cart-sibling-deal">{t("cart.sibling_deal")}</div>
            )}

            {cart.totalQuantity > 0 ? (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => { window.location.href = cart.checkoutUrl; }}
                >
                  {t("cart.proceed_checkout")}
                </button>
                <button
                  className="text-link"
                  type="button"
                  onClick={() => {
                    setCartOpen(false);
                    setShowJustAddedHero(false);
                    setExpandedLineId(null);
                  }}
                >
                  {t("cart.keep_customizing")}
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  setCartOpen(false);
                  setShowJustAddedHero(false);
                }}
              >
                {t("cart.keep_customizing")}
              </button>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
});

